import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as LucideIcons from 'lucide-react';
import { Sparkles, ChevronRight } from 'lucide-react';
import { categoriesAPI } from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import { useSelectedCity } from '@/lib/selectedCity';
import { GROUP_BY_SLUG, normalizeServerTree } from '@/lib/categoryGroups';

function toPascal(str) {
  if (!str) return '';
  return String(str)
    .split(/[-_ ]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join('');
}

/** Individual sub-category row inside a group. */
function LeafRow({ category, onClick, language, showCount }) {
  const iconKey = toPascal(category.icon);
  const IconCmp = (iconKey && LucideIcons[iconKey]) || Sparkles;
  const label = language === 'es'
    ? (category.name_es || category.name)
    : (category.name_en || category.name_es || category.name);
  const bizCount = category.business_count || 0;
  const bizLabel = language === 'es'
    ? `${bizCount} negocio${bizCount === 1 ? '' : 's'}`
    : `${bizCount} business${bizCount === 1 ? '' : 'es'}`;

  return (
    <motion.button
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className="w-full flex items-center gap-3 px-4 py-4 rounded-2xl bg-white dark:bg-[#1E1A17] border border-[#EAE3D9] dark:border-[#332D29] shadow-[0_2px_8px_rgba(42,42,42,0.03)] text-left"
      data-testid={`leaf-row-${category.slug}`}
    >
      <span className="h-11 w-11 rounded-xl bg-[#FEF3F1] dark:bg-[#332D29] grid place-items-center shrink-0">
        <IconCmp className="h-5 w-5 text-[#E85A4F]" strokeWidth={2.2} />
      </span>
      <span className="flex-1 min-w-0">
        <p className="font-body font-semibold text-[15px] text-[#2A2A2A] dark:text-[#F5F2ED] truncate">
          {label}
        </p>
        {showCount && (
          <p className="text-[12px] text-[#737373] dark:text-[#A39F99]">
            {bizLabel}
          </p>
        )}
      </span>
      <ChevronRight className="h-5 w-5 text-[#A39F99] shrink-0" />
    </motion.button>
  );
}

export default function CategoryGroupPage() {
  const { language } = useI18n();
  const navigate = useNavigate();
  const { slug: groupSlug } = useParams();
  const [city] = useSelectedCity();
  const [categories, setCategories] = useState([]);
  const [serverGroup, setServerGroup] = useState(null);
  const [loading, setLoading] = useState(true);

  // Prefer the server-computed group definition (with fresh titles, icons,
  // and category ordering set by the admin). Fall back to the flat catalog
  // + the static `GROUP_BY_SLUG` map when the endpoint is unavailable.
  const group = serverGroup || GROUP_BY_SLUG[groupSlug];

  useEffect(() => {
    let mounted = true;
    const params = city ? { city, keep_empty: true } : {};

    Promise.all([
      categoriesAPI.getGroupedTree(params).catch(() => null),
      categoriesAPI.getAll(params).catch(() => null),
    ])
      .then(([treeRes, flatRes]) => {
        if (!mounted) return;
        const tree = treeRes?.data?.groups ? treeRes.data : null;
        if (tree) {
          const normalized = normalizeServerTree(tree);
          const match = normalized.groups.find((g) => g.slug === groupSlug);
          if (match) setServerGroup(match);
        }
        const flat = Array.isArray(flatRes?.data)
          ? flatRes.data
          : flatRes?.data?.items || flatRes?.data?.data || [];
        setCategories(flat);
      })
      .finally(() => setLoading(false));
    return () => { mounted = false; };
  }, [city, groupSlug]);

  const leaves = useMemo(() => {
    if (!group) return [];
    // Server-provided groups already ship the full category objects in
    // `leafItems`, sorted the way the admin wants. Use them directly.
    if (group.leafItems && group.leafItems.length && group.leafItems[0]?.id) {
      return group.leafItems;
    }
    // Fallback: resolve leaf slugs from the flat list.
    const bySlug = {};
    (categories || []).forEach((c) => { if (c?.slug) bySlug[c.slug] = c; });
    return (group.leaves || [])
      .map((s) => bySlug[s])
      .filter(Boolean)
      .sort((a, b) => {
        const diff = (b.business_count || 0) - (a.business_count || 0);
        if (diff !== 0) return diff;
        return (a.name_es || '').localeCompare(b.name_es || '');
      });
  }, [group, categories]);

  const totalBusinesses = useMemo(
    () => leaves.reduce((sum, c) => sum + (c.business_count || 0), 0),
    [leaves]
  );

  if (!group) {
    return (
      <div className="min-h-screen bg-[#FAF7F2] dark:bg-[#12100E] pt-20 pb-24 px-5 font-body" data-testid="group-not-found">
        <h1 className="font-display font-bold text-[24px] text-[#2A2A2A] dark:text-[#F5F2ED]">
          {language === 'es' ? 'Categoría no encontrada' : 'Category not found'}
        </h1>
        <button
          type="button"
          onClick={() => navigate('/categories')}
          className="mt-4 text-[#E85A4F] font-semibold"
        >
          {language === 'es' ? 'Ver todas las categorías' : 'Browse all categories'}
        </button>
      </div>
    );
  }

  const groupLabel = language === 'es' ? group.name_es : group.name_en;
  const iconKey = toPascal(group.icon);
  const GroupIcon = (iconKey && LucideIcons[iconKey]) || Sparkles;

  return (
    <div
      className="min-h-screen bg-[#FAF7F2] dark:bg-[#12100E] pt-0 md:pt-20 pb-24 font-body"
      data-testid={`category-group-page-${group.slug}`}
    >
      <div className="px-5">
        <div className="flex items-center gap-3">
          <span className="h-11 w-11 rounded-xl bg-[#FEF3F1] dark:bg-[#332D29] grid place-items-center">
            <GroupIcon className="h-5 w-5 text-[#E85A4F]" strokeWidth={2.2} />
          </span>
          <div className="flex-1 min-w-0">
            <h1 className="font-display font-bold text-[26px] leading-tight text-[#2A2A2A] dark:text-[#F5F2ED]">
              {groupLabel}
            </h1>
            {city && totalBusinesses > 0 && (
              <p className="text-[13px] text-[#737373] dark:text-[#A39F99]">
                {language === 'es'
                  ? `${totalBusinesses} negocio${totalBusinesses === 1 ? '' : 's'} en ${city}`
                  : `${totalBusinesses} business${totalBusinesses === 1 ? '' : 'es'} in ${city}`}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="px-5 mt-6 space-y-2.5" data-testid="leaves-list">
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-16 rounded-2xl bg-white/50 dark:bg-white/5 animate-pulse" />
          ))
        ) : leaves.length ? (
          leaves.map((c) => (
            <LeafRow
              key={c.slug}
              category={c}
              language={language}
              showCount={!!city}
              onClick={() => {
                const target = city
                  ? `/search?category=${c.id || c.slug}&city=${encodeURIComponent(city)}`
                  : `/search?category=${c.id || c.slug}`;
                navigate(target);
              }}
            />
          ))
        ) : (
          <div className="text-center py-16">
            <p className="text-[#737373] dark:text-[#A39F99] text-[15px]">
              {language === 'es' ? 'Sin sub-categorías' : 'No sub-categories'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
